const state = {
  generation: 0,
  queue: [],
  isFetching: false,
  taskContext: null,
  seenOfferIds: [],
  // The Monday task ({ task_name, start_time, end_time, operation }) the current queue
  // was built for. Set from the peek response when the server resolves the task from
  // Monday (feed mode). Lets the feed detect a task switch (e.g. 10:00 boundary) and
  // drop a queue that belongs to the previous task.
  mondayTask: null
}

const mutations = {
  setQueue (state, queue) {
    state.queue = queue
  },

  shiftFromQueue (state) {
    state.queue = state.queue.slice(1)
  },

  setFetching (state, value) {
    state.isFetching = value
  },

  clearQueue (state) {
    state.generation += 1
    state.isFetching = false
    state.queue = []
    state.taskContext = null
    state.seenOfferIds = []
    state.mondayTask = null
  },

  setTaskContext (state, context) {
    state.taskContext = context
  },

  setMondayTask (state, mondayTask) {
    state.mondayTask = mondayTask
  },

  addSeenOfferId (state, offerId) {
    if (offerId && !state.seenOfferIds.includes(offerId)) {
      state.seenOfferIds.push(offerId)
    }
  }
}

const actions = {
  async prefetchUpcoming ({ commit, state, dispatch }, { taskContext, vm }) {
    if (state.isFetching) return

    const generation = state.generation
    commit('setFetching', true)
    commit('setTaskContext', taskContext)

    console.log('[Prefetch] Starting peek for next offer IDs...', taskContext)
    try {
      const peekParams = { ...taskContext, exclude_offer_ids: state.seenOfferIds }
      const response = await vm.$network_utils.peekNextOfferIds(peekParams)
      if (generation !== state.generation) return
      const offerIds = (response && response.offer_ids) || []
      console.log('[Prefetch] Got offer IDs:', offerIds)

      // Feed mode: the server resolved the current task from Monday. If it differs from
      // the task the existing queue was built for, Monday switched task (time boundary) —
      // drop the stale queue + seen list so we don't keep serving the previous task.
      const respMondayTask = response && response.monday_task
      if (respMondayTask && respMondayTask.task_name) {
        if (state.mondayTask && state.mondayTask.task_name !== respMondayTask.task_name) {
          console.log('[Prefetch] Monday task switched', state.mondayTask.task_name, '->', respMondayTask.task_name, '- dropping stale queue')
          commit('setQueue', [])
          state.seenOfferIds = []
        }
        commit('setMondayTask', respMondayTask)
      }

      // Filter out IDs already in queue AND current offer
      const currentOfferId = taskContext.current_offer_id
      const existingIds = new Set(state.queue)
      const newIds = offerIds.filter(id => !existingIds.has(id) && id !== currentOfferId)

      if (newIds.length > 0) {
        const updatedQueue = [...state.queue, ...newIds]
        commit('setQueue', updatedQueue)
        console.log('[Prefetch] Queue updated:', updatedQueue)

        // Fire-and-forget AI evaluations for each new offer
        newIds.forEach(offerId => {
          console.log('[Prefetch] Starting AI evaluation for offer', offerId)
          dispatch('aiEvaluation/fetchEvaluation', { offerId, force: false, vm }, { root: true })
            .then(() => console.log('[Prefetch] AI evaluation DONE for offer', offerId))
            .catch(() => {})
        })
      } else {
        console.log('[Prefetch] No new offer IDs to prefetch')
      }
    } catch (err) {
      console.error('[Prefetch] Error:', err)
    } finally {
      if (generation === state.generation) commit('setFetching', false)
    }
  },

  consumeNext ({ commit, state }) {
    if (state.queue.length === 0) return null
    const offerId = state.queue[0]
    commit('shiftFromQueue')
    return offerId
  }
}

const getters = {
  hasNext: state => state.queue.length > 0,
  nextOfferId: state => state.queue[0] || null,
  mondayTask: state => state.mondayTask
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
